// Il percorso di un ordine in cinque tappe e il «prossimo passo» della pagina
// ordine (redesign 2026-10-23, Davide: «modifiche più profonde, soprattutto
// per la funzionalità operativa»).
//
// Logica pura, niente React: la pagina disegna, qui si decide. Una sola fonte
// per tappe e prossimo passo, cosi' le due cose non si contraddicono mai — la
// tappa «ora» e' sempre quella di cui il prossimo passo parla.
//
// ⚠️ Mai un «Segna spedito» suggerito prima che il corriere abbia il pacco: su
// Amazon una conferma senza ritiro e' una dichiarazione falsa (regola Davide).
// Per questo la tappa «Spedito» diventa «ora» solo dopo una prova di ritiro.

import { orderHasAllServiceSku } from "./allserviceApi";
import { nomeCanaleDiretto } from "./confermaCanaleApi";
import { bonificoSiConfermaSuShopify, isBonificoDaVerificare } from "./components/BonificoButton";
import { NON_LINEAR } from "./components/fulfillmentSteps";
import { assegnatoAlMagazzino } from "./components/MagazzinoRilasciaButton";
import type { Order } from "./types";

export type TappaId = "fornitore" | "etichetta" | "ritiro" | "spedito" | "fattura";

/** ``fatto`` / ``ora`` (la prima da fare) / ``dopo`` / ``salta`` (non tocca a
 *  noi: AllService spedisce da sé, un marketplace senza fornitore da ordinare). */
export type StatoTappa = "fatto" | "ora" | "dopo" | "salta";

export interface Tappa {
  id: TappaId;
  titolo: string;
  stato: StatoTappa;
  /** Una frase sola, quella che si legge senza aprire niente. */
  riga: string;
}

/** Cio' che la pagina sa oltre all'ordine: chi lo calcola sta nei componenti. */
export interface ContestoPercorso {
  /** Fornitori a cui OM sa mandare QUESTO ordine (``useOrdinaFornitore``). */
  flussi: ReadonlyArray<{ id: string; label: string }>;
  isAllService: boolean;
  /** Pezzo assegnato dal magazzino BS COM: nessun fornitore da ordinare. */
  inMagazzino: boolean;
  /** Merce pronta e mail di ritiro da mandare (stesso gate del tasto). */
  pickupMailReady: boolean;
  /** Marketplace da avvisare direttamente («Conferma a …»), o null. */
  canaleDiretto: string | null;
  /** Consegnato: dal job del tracking o dalla lettura dal vivo. */
  consegnato: boolean;
  /** Ritiro SDA prenotato da OM che contiene l'ordine, se c'e'. */
  ritiro?: { data: string; codice: string | null; partito: boolean } | null;
  /** Bonifico del cliente da verificare prima di lavorare l'ordine. */
  bonificoDaVerificare: boolean;
}

/** Il contesto dai dati dell'ordine, piu' quello che solo la pagina sa. */
export function contestoDi(
  order: Order,
  extra: Pick<ContestoPercorso, "flussi" | "ritiro"> & { consegnatoDalVivo: boolean },
): ContestoPercorso {
  const isAllService = orderHasAllServiceSku(order);
  return {
    flussi: extra.flussi,
    isAllService,
    inMagazzino: assegnatoAlMagazzino(order),
    // Gate «Invia LDV» identico a quello della card (CardActions): AS ha il
    // suo xlsx; la mail di ritiro ha senso a merce pronta oggi / ritiro
    // pronto, o con l'allarme «LDV mai inviata» (O26-884422561615: tasto
    // sparito su un ordine gia' FULL_SHIPPED mentre l'allarme lo chiedeva).
    pickupMailReady:
      !isAllService &&
      (order.pickupReady === true ||
        order.merceProntaToday === true ||
        order.pickupMailMissing === true),
    canaleDiretto: nomeCanaleDiretto(order),
    consegnato: order.lastTrackingEvent?.delivered === true || extra.consegnatoDalVivo,
    ritiro: extra.ritiro ?? null,
    bonificoDaVerificare: isBonificoDaVerificare(order),
  };
}

export type AzionePasso =
  | { tipo: "ordina"; flusso: string }
  | { tipo: "ldv" }
  | { tipo: "ldv-allservice" }
  | { tipo: "invia-ldv" }
  | { tipo: "conferma-canale" }
  | { tipo: "spedito" }
  | { tipo: "bonifico" }
  | { tipo: "fattura" }
  | { tipo: "ritiri" };

export interface ProssimoPasso {
  /** Quanto e' urgente: decide il colore della fascia, mai il testo. */
  tono: "da-fare" | "attesa" | "fatto" | "fermo";
  titolo: string;
  perche: string;
  azione: AzionePasso | null;
  tappa: TappaId | null;
}

function giorno(iso: string): string {
  const [, mm, gg] = iso.slice(0, 10).split("-");
  return gg && mm ? `${gg}/${mm}` : iso;
}

function hasLabel(order: Order): boolean {
  return Boolean(order.trackingCode || order.ldvCode);
}

function rawData(order: Order, key: string): string | null {
  const v = order.raw?.[key];
  return typeof v === "string" && v.trim() ? v : null;
}

/** Il corriere ha il pacco: un evento di tracking che dice partito (non il
 *  vuoto «Non ancora affidata», partito=false), il ritiro OM che lo segna
 *  partito, o l'ordine gia' spedito. Senza una di queste, «spedito» sarebbe
 *  una promessa e non un fatto. */
export function ritiratoDalCorriere(order: Order, ctx: ContestoPercorso): boolean {
  return Boolean(
    order.state === "spedito" ||
    (order.lastTrackingEvent && order.lastTrackingEvent.partito !== false) ||
    ctx.consegnato ||
    ctx.ritiro?.partito,
  );
}

export function annullato(order: Order): boolean {
  const status = (order.status ?? "").trim().toUpperCase();
  return (
    order.state === "annullato" ||
    (status !== "" && status !== "SHIPPED_WITH_ERROR" && NON_LINEAR.has(status)) ||
    status.includes("CANCEL")
  );
}

type Base = { stato: "fatto" | "salta" | "da-fare"; riga: string };

function baseFornitore(order: Order, ctx: ContestoPercorso): Base {
  if (ctx.inMagazzino) return { stato: "fatto", riga: "Pezzo dal magazzino BS COM" };
  if (order.supplierOrderRef) return { stato: "fatto", riga: "Ordinato al fornitore" };
  if (order.supplierSendState === "sent") return { stato: "fatto", riga: "Ordine inviato" };
  const primo = ctx.flussi[0];
  if (order.supplierSendState === "to_send" || primo) {
    return { stato: "da-fare", riga: primo ? `Da ordinare a ${primo.label}` : "Da ordinare" };
  }
  // Nessun flusso d'invio: merce in casa o fornitore che OM non ordina.
  return { stato: "salta", riga: "Niente da ordinare da OM" };
}

function baseEtichetta(order: Order, ctx: ContestoPercorso): Base {
  if (hasLabel(order)) return { stato: "fatto", riga: "LDV fatta" };
  if (ctx.isAllService) return { stato: "salta", riga: "La spedizione la fa AllService" };
  return { stato: "da-fare", riga: "LDV da fare" };
}

function baseRitiro(order: Order, ctx: ContestoPercorso): Base {
  if (ritiratoDalCorriere(order, ctx)) return { stato: "fatto", riga: "Preso dal corriere" };
  if (ctx.isAllService) return { stato: "salta", riga: "Ritiro gestito da AllService" };
  if (ctx.pickupMailReady && !order.pickupMailSent && hasLabel(order)) {
    return { stato: "da-fare", riga: "Merce pronta: manda la LDV al fornitore" };
  }
  if (ctx.ritiro) {
    return { stato: "da-fare", riga: `Ritiro prenotato per il ${giorno(ctx.ritiro.data)}` };
  }
  if (order.pickupMailSent) return { stato: "da-fare", riga: "LDV mandata al fornitore" };
  const pronta = rawData(order, "merce_pronta_il");
  if (pronta) return { stato: "da-fare", riga: `Merce pronta dal ${giorno(pronta)}` };
  return { stato: "da-fare", riga: "In attesa della merce pronta" };
}

function baseSpedito(order: Order, ctx: ContestoPercorso): Base {
  if (ctx.consegnato) return { stato: "fatto", riga: "Consegnato al cliente" };
  if (order.state === "spedito") return { stato: "fatto", riga: "Spedito, in viaggio" };
  return { stato: "da-fare", riga: "Da segnare spedito" };
}

function baseFattura(order: Order): Base {
  const inv = order.invoice;
  if (inv?.document_number) {
    return { stato: "fatto", riga: `${inv.doc_type ?? "Documento"} n. ${inv.document_number}` };
  }
  if (inv?.status === "esclusa") return { stato: "salta", riga: "Esclusa dalla fatturazione" };
  return { stato: "da-fare", riga: "Non ancora emessa" };
}

const TITOLI: Record<TappaId, string> = {
  fornitore: "Fornitore",
  etichetta: "Etichetta",
  ritiro: "Ritiro",
  spedito: "Spedito",
  fattura: "Fattura",
};

/** Le cinque tappe, in ordine. La prima ancora da fare e' «ora», le altre
 *  aspettano: una sola tappa accesa, cosi' l'occhio sa dove andare. */
export function tappeOrdine(order: Order, ctx: ContestoPercorso): Tappa[] {
  const basi: Array<[TappaId, Base]> = [
    ["fornitore", baseFornitore(order, ctx)],
    ["etichetta", baseEtichetta(order, ctx)],
    ["ritiro", baseRitiro(order, ctx)],
    ["spedito", baseSpedito(order, ctx)],
    ["fattura", baseFattura(order)],
  ];
  let trovata = false;
  return basi.map(([id, b]) => {
    let stato: StatoTappa = b.stato === "da-fare" ? "dopo" : b.stato;
    if (b.stato === "da-fare" && !trovata) {
      stato = "ora";
      trovata = true;
    }
    return { id, titolo: TITOLI[id], stato, riga: b.riga };
  });
}

/** La cosa da fare adesso, detta in una frase, col tasto che la fa. */
export function prossimoPasso(order: Order, ctx: ContestoPercorso): ProssimoPasso {
  if (annullato(order)) {
    return {
      tono: "fermo",
      titolo: "Ordine annullato",
      perche: "Non c'è niente da spedire. Resi e rimborsi restano qui sotto.",
      azione: null,
      tappa: null,
    };
  }
  if (ctx.bonificoDaVerificare) {
    return {
      tono: "da-fare",
      titolo: "Controlla se il bonifico è arrivato",
      perche: bonificoSiConfermaSuShopify(order)
        ? "Quando lo confermi, OM lo segna pagato anche su Shopify."
        : "Finché il pagamento non c'è, l'ordine non si lavora.",
      azione: { tipo: "bonifico" },
      tappa: null,
    };
  }
  const ora = tappeOrdine(order, ctx).find((t) => t.stato === "ora");
  if (!ora) {
    return {
      tono: "fatto",
      titolo: "Tutto fatto",
      perche: "Ordinato, spedito e fatturato: per questo ordine non resta niente.",
      azione: null,
      tappa: null,
    };
  }
  switch (ora.id) {
    case "fornitore": {
      const primo = ctx.flussi[0];
      return {
        tono: "da-fare",
        titolo: primo ? `Ordina a ${primo.label}` : "Manda l'ordine al fornitore",
        perche: "Il fornitore non ha ancora ricevuto questo ordine.",
        azione: primo ? { tipo: "ordina", flusso: primo.id } : null,
        tappa: "fornitore",
      };
    }
    case "etichetta":
      return {
        tono: "da-fare",
        titolo: "Genera la LDV",
        perche: "Senza etichetta il corriere non può ritirare il pacco.",
        azione: { tipo: "ldv" },
        tappa: "etichetta",
      };
    case "ritiro":
      if (ctx.pickupMailReady && !order.pickupMailSent && hasLabel(order)) {
        return {
          tono: "da-fare",
          titolo: "Manda la LDV al fornitore",
          perche: "La merce è pronta: il fornitore aspetta l'etichetta per il ritiro.",
          azione: { tipo: "invia-ldv" },
          tappa: "ritiro",
        };
      }
      return {
        tono: "attesa",
        titolo: ctx.ritiro
          ? `Ritiro prenotato per il ${giorno(ctx.ritiro.data)}`
          : "Aspetta il ritiro del corriere",
        perche: ctx.ritiro
          ? "Quando il corriere scansiona il pacco, l'ordine passa a «Spedito»."
          : "Lo segniamo spedito solo quando il corriere ha il pacco in mano.",
        azione: { tipo: "ritiri" },
        tappa: "ritiro",
      };
    case "spedito":
      return {
        tono: "da-fare",
        titolo: ctx.canaleDiretto ? `Conferma a ${ctx.canaleDiretto}` : "Segna spedito",
        perche: "Il corriere ha il pacco: il cliente deve ricevere la mail con il tracking.",
        azione: ctx.canaleDiretto ? { tipo: "conferma-canale" } : { tipo: "spedito" },
        tappa: "spedito",
      };
    case "fattura":
      return {
        tono: "attesa",
        titolo: "Manca la fattura",
        perche: "La emette il giro delle 18, o il tasto Emetti in Fatturazione.",
        azione: { tipo: "fattura" },
        tappa: "fattura",
      };
  }
}
